import { API_CONFIG, API_ENDPOINTS } from '@/constants/api';
import { getAuthToken } from '@/utils/auth';
import { apiClient, ApiResponse } from './apiClient';

export type MedicalRecordStatus = 'DRAFT' | 'FINALIZED';

export type MedicalRecord = {
  id: string;
  appointmentId: string;
  patientId: string;
  doctorId: string;
  content: string;
  clinicalSummary?: string | null;
  diagnosisCode?: string | null;
  status: MedicalRecordStatus;
  finalizedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type SaveMedicalRecordPayload = {
  appointmentId: string;
  content?: string;
  clinicalSummary?: string;
  diagnosisCode?: string;
  status?: MedicalRecordStatus;
};

export type MedicalRecordPdfDownload = {
  blob: Blob;
  fileName: string;
};

/** Contrato oficial: POST /api/medical-records */
export const MEDICAL_RECORD_SAVE_METHOD = 'POST' as const;
export const MEDICAL_RECORD_SAVE_PATH = API_ENDPOINTS.MEDICAL_RECORDS.SAVE;

/**
 * Busca prontuário da consulta (GET /medical-records/appointment/:id)
 */
export async function getMedicalRecordByAppointment(
  appointmentId: string,
): Promise<ApiResponse<MedicalRecord | null>> {
  return apiClient.get<MedicalRecord | null>(
    API_ENDPOINTS.MEDICAL_RECORDS.BY_APPOINTMENT(appointmentId),
  );
}

/**
 * Salva/atualiza prontuário eletrônico (POST /medical-records)
 * Método e path são fixos — não usar PUT/PATCH/GET neste recurso.
 */
export async function saveMedicalRecord(
  payload: SaveMedicalRecordPayload,
): Promise<ApiResponse<MedicalRecord>> {
  if (import.meta.env.DEV) {
    console.log('[medicalRecords] salvar', {
      method: MEDICAL_RECORD_SAVE_METHOD,
      path: MEDICAL_RECORD_SAVE_PATH,
      baseUrl: API_CONFIG.BASE_URL,
      appointmentId: payload.appointmentId,
      status: payload.status ?? 'DRAFT',
    });
  }

  return apiClient.post<MedicalRecord>(MEDICAL_RECORD_SAVE_PATH, payload);
}

export async function downloadPatientMedicalRecordPdf(
  patientId: string,
): Promise<MedicalRecordPdfDownload> {
  const token = getAuthToken();
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.MEDICAL_RECORDS.PATIENT_PDF(patientId)}`,
    {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );

  if (!response.ok) {
    const errorMessage = await readErrorMessage(response);
    throw new Error(errorMessage || 'Não foi possível exportar o prontuário.');
  }

  const blob = await response.blob();
  return {
    blob,
    fileName: getFileName(response.headers.get('content-disposition')),
  };
}

async function readErrorMessage(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return response.statusText;
  }

  const body = (await response.json()) as { error?: string; message?: string };
  return body.error || body.message || response.statusText;
}

function getFileName(contentDisposition: string | null): string {
  if (!contentDisposition) return 'prontuario-paciente.pdf';

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1] || 'prontuario-paciente.pdf';
}
